import {
  buildScenarioRecoveryId,
  buildScenarioRecoveryKey,
  clearScenarioRecoveryDraft,
  loadScenarioRecoveryDraft,
  ScenarioRecoveryData,
  saveScenarioRecoveryDraft,
} from "@Containers/game/builder/functions/recovery-storage";
import { getScenarioStateFromData, loadScenario, saveScenario } from "@Containers/game/builder/functions/scenario-functions";
import { BuilderState, StateChangeSource } from "@Containers/game/builder/models/builder-data-types";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { useEffect, useMemo, useRef, useState } from "react";

const RECOVERY_SAVE_DEBOUNCE_MS = 750;

export const useScenarioRecovery = ({
  scenarioMetadata,
  state,
  dispatch,
}: {
  scenarioMetadata?: ScenarioResponse;
  state: BuilderState;
  dispatch: (data: any) => void;
}) => {
  const [recoveryDraft, setRecoveryDraft] = useState<ScenarioRecoveryData | null>(null);
  const checkedRecoveryKeyRef = useRef<string | null>(null);
  const lastSavedDraftRef = useRef<string | null>(null);

  const recoveryScenarioId = useMemo(() => buildScenarioRecoveryId(scenarioMetadata), [scenarioMetadata]);
  const recoveryKey = useMemo(() => buildScenarioRecoveryKey(recoveryScenarioId), [recoveryScenarioId]);

  useEffect(() => {
    if (!recoveryKey || state.clip.isDirty || checkedRecoveryKeyRef.current === recoveryKey) {
      return;
    }

    checkedRecoveryKeyRef.current = recoveryKey;
    const draft = loadScenarioRecoveryDraft(recoveryScenarioId);
    if (draft) {
      setRecoveryDraft(draft);
    }
  }, [recoveryScenarioId, recoveryKey, state.clip.isDirty]);

  useEffect(() => {
    if (!recoveryScenarioId || !recoveryKey || !state.clip.isDirty) {
      return;
    }

    const timeout = window.setTimeout(() => {
      const scenarioData = saveScenario(state.clip, state.field);
      const serializedScenario = JSON.stringify(scenarioData);

      if (lastSavedDraftRef.current === serializedScenario) {
        return;
      }

      if (saveScenarioRecoveryDraft(recoveryScenarioId, scenarioData)) {
        lastSavedDraftRef.current = serializedScenario;
      }
    }, RECOVERY_SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [recoveryScenarioId, recoveryKey, state.clip, state.field]);

  const restoreRecoveryDraft = () => {
    if (!recoveryDraft) return;

    const scenarioState = getScenarioStateFromData(recoveryDraft.data, state.field, []);
    loadScenario(scenarioState, StateChangeSource.Load, state, dispatch, false, true);
    clearScenarioRecoveryDraft(recoveryScenarioId);
    setRecoveryDraft(null);
    lastSavedDraftRef.current = JSON.stringify(recoveryDraft.data);
  };

  const discardRecoveryDraft = () => {
    clearScenarioRecoveryDraft(recoveryScenarioId);
    setRecoveryDraft(null);
  };

  return {
    recoveryDraft,
    recoverySavedAt: recoveryDraft ? new Date(recoveryDraft.savedAt).toLocaleString() : "",
    restoreRecoveryDraft,
    discardRecoveryDraft,
  };
};
